import { adImageAlt, adSlug } from "@/lib/seo";
import { imageSrcSet, optimizedImage } from "@/lib/img";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  BadgeCheck,
  Camera,
  Crown,
  Eye,
  Flame,
  Heart,
  MapPin,
  Play,
  Rocket,
  Sparkles,
  Store,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrice, type Ad } from "@/lib/data";
import { TimeAgo } from "@/components/time-ago";
import { useAuth } from "@/hooks/use-auth";
import { useStaffRole } from "@/components/user-menu";
import { daysLeft } from "@/lib/listings";
import { useFavorites } from "@/lib/favorites";
import { StoreVideo } from "@/components/store-video";
import { toast } from "sonner";

export function AdCard({ ad, index = 0 }: { ad: Ad; index?: number }) {
  const { user } = useAuth();
  const role = useStaffRole(user?.id);
  const { isFavorite, toggle } = useFavorites();
  const liked = isFavorite(ad.id);
  const canSeePackage =
    Boolean(user) && (ad.ownerId === user?.id || role === "admin" || role === "moderator");
  const left = daysLeft(ad.badgeExpiresAt);
  const showPackage = canSeePackage && ad.badge !== "none" && left !== null;
  const expiringSoon = left !== null && left < 3;
  const isPremium = ad.badge === "premium" || ad.adminPremium;
  const isVip = ad.badge === "vip" || ad.adminVip;
  const urgent = (ad.specs ?? {})["_admin_tecili"] === "1" || isVip;
  const isSold = ad.status === "sold";
  const isStoreCard = Boolean(ad.isStore);
  const delayStyle = { animationDelay: `${Math.min(index, 8) * 45}ms` };

  const cardContent = (
    <>
      <Link
        to="/elan/$id"
        params={{ id: adSlug(ad) }}
        className="flex min-w-0 flex-col"
      >
        <div
          className="relative aspect-[4/3] w-full overflow-hidden rounded-t-[18px] bg-muted sm:aspect-[16/10]"
          style={{ containerType: "inline-size" }}
        >
          {ad.videoUrl ? (
            <StoreVideo src={ad.videoUrl} poster={ad.images[0]} />
          ) : (
            <img
              src={optimizedImage(ad.images[0] ?? "", 400)}
              srcSet={imageSrcSet(ad.images[0] ?? "", 400)}
              alt={adImageAlt(ad)}
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "low"}
              decoding="async"
              sizes="(max-width: 640px) 50vw, (max-width: 1280px) 33vw, 20vw"
              width={400}
              height={250}
              className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.07]"
            />
          )}

          {/* Su nişanı — şəklin tam ortasında */}
          <span aria-hidden className="img-watermark">
            albazar.az
          </span>

          {isSold && (
            <span className="absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-1 text-[10px] font-black uppercase leading-none tracking-wide text-destructive-foreground shadow ring-1 ring-black/10">
              <BadgeCheck className="size-3" /> Satıldı
            </span>
          )}

          <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1">
            {ad.images.length > 1 && (
              <span className="inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-1 text-[10px] font-bold leading-none text-white backdrop-blur">
                <Camera className="size-3" /> {ad.images.length} foto
              </span>
            )}
            {ad.videoUrl && (
              <span className="inline-flex items-center gap-1 rounded-md bg-info/90 px-1.5 py-1 text-[10px] font-bold leading-none text-info-foreground backdrop-blur">
                <Play className="size-3 fill-current" /> Video
              </span>
            )}
            {(ad.isStore || ad.videoUrl) && (
              <span className="inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-1 text-[10px] font-bold leading-none text-white backdrop-blur">
                <Store className="size-3" /> Mağaza
              </span>
            )}
          </div>

          <div className="absolute left-2 top-2 z-10 flex flex-wrap gap-1">
            {urgent && (
              <span className="inline-flex items-center gap-1 rounded-md bg-destructive px-1.5 py-1 text-[10px] font-bold uppercase leading-none tracking-wide text-destructive-foreground shadow ring-1 ring-black/10">
                <Flame className="size-3" /> Təcili
              </span>
            )}
            {isPremium && (
              <span className="badge-premium-grad inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-bold uppercase leading-none tracking-wide shadow ring-1 ring-black/10">
                <Sparkles className="size-3" /> Premium
              </span>
            )}
            {isVip && (
              <span className="inline-flex items-center gap-1 rounded-md bg-vip px-1.5 py-1 text-[10px] font-bold leading-none tracking-wide text-vip-foreground shadow ring-1 ring-black/10">
                <Crown className="size-3" /> VIP
              </span>
            )}
          </div>
        </div>

        <div className="space-y-0.5 p-2 pb-1.5">
          <h3 className="line-clamp-2 min-h-[2rem] text-[13px] font-medium leading-[16px] text-foreground/90 sm:text-sm sm:leading-[17px]">
            {ad.title}
          </h3>
          <p className="flex items-center gap-1.5 text-base font-black leading-5 text-primary sm:text-lg sm:leading-6">
            {formatPrice(ad.price)}
            {isSold && (
              <span className="text-[11px] font-black uppercase tracking-wide text-destructive">
                Satıldı
              </span>
            )}
          </p>
          {showPackage && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold leading-none text-foreground">
                {Number(ad.badgePrice ?? 0).toFixed(0)} AZN
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold leading-none",
                  expiringSoon
                    ? "bg-destructive/15 text-destructive"
                    : "bg-success/15 text-success",
                )}
              >
                {expiringSoon && <AlertTriangle className="size-3" />}
                {left} gün qaldı
              </span>
            </div>
          )}
          <p className="flex items-center gap-1 text-[11px] leading-none text-muted-foreground">
            <MapPin className="size-3" /> {ad.city}
          </p>
          <p className="flex items-center gap-2 text-[11px] leading-none text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Eye className="size-3" /> {ad.views}
            </span>
            <TimeAgo iso={ad.createdAt} />
          </p>
        </div>
      </Link>

      <div className="mt-auto flex items-center gap-1.5 px-2 pb-2">
        <Link
          to="/elan-reklam/$id"
          params={{ id: ad.id }}
          search={{ plan: "premium" as const, days: 7 }}
          className="btn-promote-gradient inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-full px-2.5 text-[11px] font-bold transition-transform active:scale-95"
        >
          <Rocket className="size-3.5" /> Önə çıxar
        </Link>
        <button
          type="button"
          aria-label={liked ? "Seçilmişlərdən çıxar" : "Seçilmişlərə əlavə et"}
          aria-pressed={liked}
          onClick={() => {
            const added = toggle(ad);
            toast.success(added ? "Seçilmişlərə əlavə olundu" : "Seçilmişlərdən çıxarıldı");
          }}
          className={cn(
            "grid size-7 shrink-0 place-items-center rounded-full border border-border transition-all active:scale-90",
            liked ? "bg-destructive/10 text-destructive" : "bg-card text-muted-foreground",
          )}
        >
          <Heart className={cn("size-3.5", liked && "fill-destructive")} strokeWidth={2.2} />
        </button>
      </div>
    </>
  );

  return isStoreCard ? (
    <div
      className="store-card-frame rounded-[18px] p-[2.5px] transition-all duration-300 ease-out hover:-translate-y-1"
      style={delayStyle}
    >
      <div className="group flex w-full min-w-0 animate-fade-up flex-col overflow-hidden rounded-[15.5px] border border-border/40 bg-card transition-colors duration-300 ease-out hover:border-primary/40">
        {cardContent}
      </div>
    </div>
  ) : (
    <div
      className="group flex w-full min-w-0 animate-fade-up flex-col overflow-hidden rounded-[18px] border border-border bg-card shadow-card transition-all duration-300 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-elevated"
      style={delayStyle}
    >
      {cardContent}
    </div>
  );
}

